/**
 * @fileoverview Error Boundary Component
 * @description React Error Boundary with sanitized minimalist UI for system failures.
 */

import React from 'react';
import { AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
      errorId: `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 6).toUpperCase()}`
    };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    // In production, log to a service here
    if (process.env.NODE_ENV === 'development') {
      console.error('[ErrorBoundary] Error occurred:', error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, errorId: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[80vh] flex items-center justify-center p-6 bg-[#f8fafc] font-sans">
          <div className="w-full max-w-3xl bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden animate-in fade-in zoom-in duration-300">
            <div className="p-10 flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mb-6">
                <AlertCircle className="h-8 w-8 text-red-500" strokeWidth={2.5} />
              </div>

              <h2 className="text-xl font-semibold text-slate-900 tracking-tight mb-2">System Encountered an Error</h2>
              <p className="text-slate-500 text-sm max-w-sm leading-relaxed mb-8">
                The application encountered an unexpected state. You can attempt to restore the session or reload the interface.
              </p>

              <div className="flex items-center space-x-3 w-full">
                <Button
                  onClick={this.handleRetry}
                  className="flex-1 h-auto px-4 py-2.5 bg-slate-900 text-white rounded-xl text-[12px] font-medium uppercase tracking-widest hover:bg-black transition-all"
                >
                  Restore Session
                </Button>
                <Button
                  variant="outline"
                  onClick={this.handleReload}
                  className="flex-1 h-auto px-4 py-2.5 bg-white text-slate-700 border-slate-200 rounded-xl text-[12px] font-medium uppercase tracking-widest hover:bg-slate-50 transition-all"
                >
                  Reload App
                </Button>
              </div>
            </div>

            <div className="bg-slate-50 p-6 border-t border-slate-100">
              <details className="group">
                <summary className="text-[10px] font-medium text-slate-400 uppercase tracking-widest cursor-pointer hover:text-slate-600 transition-colors flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-red-400 rounded-full mr-2" />
                    Technical Diagnostics
                  </div>
                  <span className="text-[9px] bg-slate-200 px-2 py-1 rounded text-slate-500 group-open:hidden">REVEAL</span>
                </summary>
                <div className="mt-4 relative">
                  <div className="p-4 bg-slate-900 rounded-lg overflow-x-auto max-h-64 custom-scrollbar">
                    <pre className="text-[11px] font-mono text-red-400 leading-relaxed whitespace-pre-wrap">
                      {this.state.error?.toString()}
                      {"\n\n"}
                      {this.state.errorInfo?.componentStack}
                    </pre>
                  </div>
                  <button
                    onClick={() => {
                      const text = `${this.state.error?.toString()}\n\n${this.state.errorInfo?.componentStack}`;
                      navigator.clipboard.writeText(text);
                    }}
                    className="absolute top-2 right-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-[10px] font-medium uppercase tracking-wider rounded-lg backdrop-blur-sm transition-colors"
                  >
                    Copy Error
                  </button>
                </div>
              </details>
            </div>

            <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-100 flex justify-center">
              <p className="text-[9px] font-medium text-slate-300 uppercase tracking-[0.2em]">Error ID: {this.state.errorId}</p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export const withErrorBoundary = (Component) => {
  return (props) => (
    <ErrorBoundary>
      <Component {...props} />
    </ErrorBoundary>
  );
};

export default ErrorBoundary;
